import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Send, Paperclip,
  Check, CheckCheck, Reply, ChevronDown, ChevronUp
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalMessages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('case_id');

  const [conversations, setConversations] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(caseId || '');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessage, setNewMessage] = useState({ subject: '', content: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedConvos, setExpandedConvos] = useState(new Set());
  const [attachments, setAttachments] = useState([]);
  const replyInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCases();
    loadMessages();
  }, [caseId]);

  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  const loadCases = async () => {
    try {
      const data = await clientPortalService.getCases();
      setCases(data);
      if (!selectedCase && data.length > 0) {
        setSelectedCase(data[0].case_id);
      }
    } catch (err) {
      console.error('Failed to load cases:', err);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService.getMessages(caseId || null);
      setConversations(data.conversations || []);
      // Root bubbles render in full on this page, so a loaded root has been
      // seen -- mark it so, rather than waiting for a click nobody makes.
      // Replies stay unread until their thread is expanded.
      (data.conversations || []).forEach(convo => {
        const root = convo.root;
        if (!root.is_from_client && root.status === 'unread') {
          clientPortalService.markMessageRead(root.message_id).catch(() => {});
        }
      });
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedCase || !newMessage.content.trim()) return;
    try {
      setSending(true);
      const result = await clientPortalService.sendMessage(selectedCase, newMessage.content, newMessage.subject || null);

      // Upload attachments to the new message
      const failed = [];
      for (const file of attachments) {
        try {
          await clientPortalService.uploadMessageAttachment(result.message_id, file);
        } catch (err) {
          console.error('Failed to upload attachment:', err);
          const reason = err.response?.data?.detail || 'upload failed';
          failed.push(`${file.name} (${reason})`);
        }
      }
      if (failed.length > 0) {
        alert(`Your message was sent, but ${failed.length === 1 ? 'this attachment was' : 'these attachments were'} NOT delivered:\n\n${failed.join('\n')}\n\nPlease try attaching again or contact the office.`);
      }

      setNewMessage({ subject: '', content: '' });
      setAttachments([]);
      setShowNewMessage(false);
      loadMessages();
    } catch (err) {
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyingTo || !replyContent.trim()) return;
    try {
      setSending(true);
      const rootMsg = conversations.find(c => c.root.message_id === replyingTo.message_id);
      const caseIdForReply = rootMsg?.root?.case_id || selectedCase;
      await clientPortalService.sendMessage(caseIdForReply, replyContent, null, replyingTo.message_id);
      setReplyContent('');
      setReplyingTo(null);
      setExpandedConvos(prev => new Set([...prev, replyingTo.message_id]));
      loadMessages();
    } catch (err) {
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => f.size <= 100 * 1024 * 1024);
    if (valid.length < files.length) {
      alert('Some files were skipped (max 100MB per file)');
    }
    setAttachments(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // A copied file or screenshot pastes as clipboardData.files, which a plain
  // textarea silently ignores -- route it into attachments instead. Text
  // pastes fall through to the browser's native handling.
  const handleComposerPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length === 0) return;
    e.preventDefault();
    const valid = files.filter(f => f.size <= 100 * 1024 * 1024);
    if (valid.length < files.length) {
      alert('Some files were skipped (max 100MB per file)');
    }
    setAttachments(prev => [...prev, ...valid]);
  };

  // Replies don't support attachments -- say so instead of doing nothing.
  const handleReplyPaste = (e) => {
    if ((e.clipboardData?.files || []).length > 0) {
      e.preventDefault();
      alert('To send a file, use New Message and Attach files — or paste the file there.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const markAsRead = async (messageId) => {
    try {
      await clientPortalService.markMessageRead(messageId);
      setConversations(prev => prev.map(convo => ({
        ...convo,
        root: convo.root.message_id === messageId ? { ...convo.root, status: 'read' } : convo.root,
        replies: convo.replies.map(r => r.message_id === messageId ? { ...r, status: 'read' } : r)
      })));
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const toggleConvo = (rootId) => {
    const isOpening = !expandedConvos.has(rootId);
    setExpandedConvos(prev => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
    if (isOpening) {
      const convo = conversations.find(c => c.root.message_id === rootId);
      (convo?.replies || []).forEach(r => {
        if (!r.is_from_client && r.status === 'unread') markAsRead(r.message_id);
      });
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const AttachmentList = ({ messageId }) => {
    const [atts, setAtts] = useState(null);

    useEffect(() => {
      clientPortalService.getMessageAttachments(messageId)
        .then(data => setAtts(data))
        .catch(() => setAtts([]));
    }, [messageId]);

    const handleDownload = async (att) => {
      // iOS Safari <a download> no-op — open a tab in-gesture; non-iOS byte-identical.
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const iosWin = isIOS ? window.open('', '_blank') : null;
      try {
        const blob = await clientPortalService.downloadMessageAttachment(att.id);
        if (isIOS) {
          const iosType = /\.pdf$/i.test(att.file_name || '') ? 'application/pdf' : (blob.type || 'application/octet-stream');
          const iosUrl = window.URL.createObjectURL(new Blob([blob], { type: iosType }));
          if (iosWin) { iosWin.location = iosUrl; }
          else { alert('Please allow pop-ups for this site to open attachments, then tap again.'); }
          setTimeout(() => window.URL.revokeObjectURL(iosUrl), 60000);
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', att.file_name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        if (iosWin) iosWin.close();
        console.error('Download failed:', err);
      }
    };

    if (!atts) return (
      <div className="mt-2 flex items-center text-xs text-virgo-teal-500">
        <Paperclip size={12} className="mr-1" />
        <span>Loading...</span>
      </div>
    );

    return (
      <div className="mt-2 space-y-1">
        {atts.map(att => (
          <button
            key={att.id}
            onClick={() => handleDownload(att)}
            className="flex items-center text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <Paperclip size={12} className="mr-1" />
            <span>{att.file_name} ({formatFileSize(att.file_size)})</span>
          </button>
        ))}
      </div>
    );
  };

  const MessageBubble = ({ msg, isReply = false }) => {
    const isClient = msg.is_from_client;
    const isUnread = !isClient && msg.status === 'unread';

    return (
      <div
        className={`flex ${isClient ? 'justify-end' : 'justify-start items-start space-x-2'} ${isReply ? 'ml-8' : ''}`}
        onClick={() => isUnread && markAsRead(msg.message_id)}
      >
        {!isClient && (
          // Attorney/firm messages: show the sender's initials, never the AI mascot —
          // a client must not see an AI avatar attached to their lawyer's words.
          <div className="w-8 h-8 rounded-full bg-cyan-700/40 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-1">
            <span className="text-xs font-semibold text-cyan-200">
              {(msg.sender_name || 'Firm').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </span>
          </div>
        )}
        <div className={`max-w-[75%] ${isClient
          ? 'bg-gradient-to-br from-cyan-600/40 to-blue-600/30 border-cyan-500/30'
          : isUnread
            ? 'bg-gradient-to-br from-virgo-navy-600 to-virgo-navy-700 border-cyan-500/50'
            : 'bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 border-virgo-teal-700/30'
        } rounded-2xl ${isClient ? 'rounded-br-sm' : 'rounded-bl-sm'} border p-4`}>
          {!isClient && (
            <p className="text-xs font-medium text-cyan-400 mb-1">{msg.sender_name}</p>
          )}
          {msg.subject && !isReply && (
            <p className="text-sm font-semibold text-white mb-1">{msg.subject}</p>
          )}
          <p className="text-virgo-teal-200 text-sm whitespace-pre-wrap">{msg.content}</p>
          <div className="flex items-center justify-end space-x-2 mt-2">
            <span className="text-xs text-virgo-teal-500">{formatTime(msg.created_at)}</span>
            {isClient && (
              msg.read_at
                ? <CheckCheck size={12} className="text-blue-400" />
                : <Check size={12} className="text-virgo-teal-500" />
            )}
          </div>
          {msg.has_attachments && (
            <AttachmentList messageId={msg.message_id} />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/portal/dashboard')} className="text-virgo-teal-400 hover:text-white transition-colors">
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center space-x-3">
              <MessageSquare className="text-cyan-400" size={24} />
              <h1 className="text-xl font-bold text-white">Sigil</h1>
            </div>
          </div>
          <button
            onClick={() => setShowNewMessage(true)}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium transition-all text-sm"
          >
            New Message
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Case Filter */}
        <div className="mb-6">
          <select
            value={selectedCase}
            onChange={(e) => {
              setSelectedCase(e.target.value);
              navigate(e.target.value ? `/portal/messages?case_id=${e.target.value}` : '/portal/messages');
            }}
            className="w-full max-w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Cases</option>
            {cases.map(c => (
              <option key={c.case_id} value={c.case_id}>{c.case_name}</option>
            ))}
          </select>
        </div>

        {/* New Message Form */}
        {showNewMessage && (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/30 p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">New Message</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Case</label>
                <select
                  value={selectedCase}
                  onChange={(e) => setSelectedCase(e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select a case...</option>
                  {cases.map(c => (
                    <option key={c.case_id} value={c.case_id}>{c.case_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Subject (optional)</label>
                <input
                  type="text"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="Message subject..."
                />
              </div>
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Message</label>
                <textarea
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  onPaste={handleComposerPaste}
                  rows={4}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="Type your message..."
                />
              </div>
              {/* Attachments */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 text-sm text-virgo-teal-400 hover:text-cyan-400 transition-colors"
                >
                  <Paperclip size={16} />
                  <span>Attach files</span>
                </button>
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((file, idx) => (
                      <span key={idx} className="inline-flex items-center bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg px-3 py-1 text-xs text-virgo-teal-300">
                        <Paperclip size={12} className="mr-1" />
                        {file.name} ({formatFileSize(file.size)})
                        <button onClick={() => removeAttachment(idx)} className="ml-2 text-virgo-teal-500 hover:text-red-400">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowNewMessage(false); setNewMessage({ subject: '', content: '' }); setAttachments([]); }}
                  className="px-4 py-2 border border-virgo-teal-700/50 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={sending || !selectedCase || !newMessage.content.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2 transition-all"
                >
                  <Send size={16} />
                  <span>{sending ? 'Sending...' : 'Send'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conversations */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-12 text-center">
            <MessageSquare className="text-virgo-teal-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-white mb-2">No Messages Yet</h2>
            <p className="text-virgo-teal-400">Start a conversation with your attorney.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conversations.map((convo) => {
              const isExpanded = expandedConvos.has(convo.root.message_id);
              const hasReplies = convo.replies && convo.replies.length > 0;
              const isReplying = replyingTo?.message_id === convo.root.message_id;

              return (
                <div
                  key={convo.root.message_id}
                  className="bg-virgo-navy-800/30 rounded-xl border border-virgo-teal-700/20 p-4"
                >
                  {/* Case tag */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-virgo-teal-500 bg-virgo-navy-700 px-2 py-0.5 rounded">
                      {convo.root.case_name}
                    </span>
                    <span className="text-xs text-virgo-teal-500">{formatTime(convo.last_activity)}</span>
                  </div>

                  {/* Root message */}
                  <MessageBubble msg={convo.root} />

                  {/* Replies toggle */}
                  {hasReplies && (
                    <button
                      onClick={() => toggleConvo(convo.root.message_id)}
                      className="flex items-center space-x-1 text-xs text-cyan-400 hover:text-cyan-300 mt-3 ml-2 transition-colors"
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      <span>{convo.replies.length} {convo.replies.length === 1 ? 'reply' : 'replies'}</span>
                    </button>
                  )}

                  {/* Replies */}
                  {isExpanded && hasReplies && (
                    <div className="space-y-3 mt-3 border-l-2 border-virgo-teal-700/20 pl-2">
                      {convo.replies.map(reply => (
                        <MessageBubble key={reply.message_id} msg={reply} isReply />
                      ))}
                    </div>
                  )}

                  {/* Reply action */}
                  {!isReplying && (
                    <button
                      onClick={() => {
                        setReplyingTo({ message_id: convo.root.message_id, subject: convo.root.subject, sender_name: convo.root.sender_name });
                        setExpandedConvos(prev => new Set([...prev, convo.root.message_id]));
                      }}
                      className="flex items-center space-x-1 text-xs text-virgo-teal-400 hover:text-cyan-400 mt-3 ml-2 transition-colors"
                    >
                      <Reply size={14} />
                      <span>Reply</span>
                    </button>
                  )}

                  {/* Inline reply input */}
                  {isReplying && (
                    <div className="mt-3 flex items-end space-x-2">
                      <textarea
                        ref={replyInputRef}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        onPaste={handleReplyPaste}
                        rows={2}
                        className="flex-1 px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 resize-none"
                        placeholder="Type your reply..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                          if (e.key === 'Escape') {
                            setReplyingTo(null);
                            setReplyContent('');
                          }
                        }}
                      />
                      <button
                        onClick={handleSendReply}
                        disabled={sending || !replyContent.trim()}
                        className="p-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg disabled:opacity-50 transition-all"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalMessages;
